import React from 'react';
import { DataTable } from '@/components/ui/datatable';
import { getDataRetentionColumns } from './columns';
import { DataRetentionRecordUI, toUI, fromUI } from '../dataTypeMapping';
import { DataRetentionSchedule } from '@/js/external-js/variableModules/namespaces/DataRetentionSchedule.namespace';
import DataRetentionTableCards from './DataRetentionTableCards';
import { Loader2 } from 'lucide-react';
type DataTypeEntity = DataRetentionSchedule.DataTypeEntity;

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

interface DataRetentionTableProps {
  records: DataTypeEntity[];
  isLoading?: boolean;
  onView: (record: DataTypeEntity) => void;
  onDelete: (id: number) => void;
  pageIndex?: number;
  onPageIndexChange?: (pageIndex: number) => void;
}

const DataRetentionTable: React.FC<DataRetentionTableProps> = ({ records, isLoading = false, onView, onDelete, pageIndex, onPageIndexChange }) => {
  const isMobile = useIsMobile();
  const uiRecords: DataRetentionRecordUI[] = records.map(toUI);
  const handleView = (uiRecord: DataRetentionRecordUI) => {
    onView(fromUI(uiRecord));
  };
  const handleDelete = (uiRecord: DataRetentionRecordUI) => {
    onDelete(uiRecord.dataTypeID);
  };
  const columns = React.useMemo(() => getDataRetentionColumns(handleView, handleDelete), [onView, onDelete]);
  
  if (isMobile) {
    return (
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading data types...</span>
            </div>
          </div>
        )}
        <DataRetentionTableCards 
          records={uiRecords} 
          onView={handleView} 
          pageSize={10}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
        />
      </div>
    );
  }
  
  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading data types...</span>
          </div>
        </div>
      )}
      <DataTable 
        columns={columns} 
        data={uiRecords} 
        pageSize={10} 
        onRowClick={(record) => handleView(record)}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
      />
    </div>
  );
};

export default DataRetentionTable;
